import {
  IResult,
  ICategorizedResponse,
  GuestsSelection,
  CalendarPriceBucket,
  IMonthBucket,
} from "redmond";

import * as actionTypes from "./constants";
import { IHotelSearchState } from "../reducer/state";
import { StayType } from "redmond/hotels-module/interfaces";

export interface IFetchLocationCategories {
  type: actionTypes.FETCH_LOCATION_CATEGORIES;
  queryString: string;
}

export const fetchLocationCategories = (
  queryString: string
): IFetchLocationCategories => ({
  type: actionTypes.FETCH_LOCATION_CATEGORIES,
  queryString,
});

export interface ISetLocationCategories {
  type: actionTypes.SET_LOCATION_CATEGORIES;
  categories: ICategorizedResponse[];
}

export const setLocationCategories = (
  categories: ICategorizedResponse[]
): ISetLocationCategories => ({
  type: actionTypes.SET_LOCATION_CATEGORIES,
  categories,
});

export interface ISetLocation {
  type: actionTypes.SET_LOCATION;
  location: IResult | null;
}

export const setLocation = (location: IResult | null): ISetLocation => ({
  type: actionTypes.SET_LOCATION,
  location,
});

export interface ISetFromDate {
  type: actionTypes.SET_FROM_DATE;
  date: Date | null;
}

export const setFromDate = (date: Date | null): ISetFromDate => ({
  type: actionTypes.SET_FROM_DATE,
  date,
});

export interface ISetUntilDate {
  type: actionTypes.SET_UNTIL_DATE;
  date: Date | null;
}

export const setUntilDate = (date: Date | null): ISetUntilDate => ({
  type: actionTypes.SET_UNTIL_DATE,
  date,
});

export interface ISetOccupancyCounts {
  type: actionTypes.SET_OCCUPANCY_COUNTS;
  counts: Omit<GuestsSelection, "rooms">;
}

export const setOccupancyCounts = (
  counts: Omit<GuestsSelection, "rooms">
): ISetOccupancyCounts => ({
  type: actionTypes.SET_OCCUPANCY_COUNTS,
  counts,
});

export interface ISetLocationAutocompleteError {
  type: actionTypes.SET_LOCATION_AUTOCOMPLETE_ERROR;
  error: boolean;
}

export const setLocationAutocompleteError = (
  error: boolean
): ISetLocationAutocompleteError => ({
  type: actionTypes.SET_LOCATION_AUTOCOMPLETE_ERROR,
  error,
});

export interface ISetRoomsCount {
  type: actionTypes.SET_ROOMS_COUNT;
  rooms: number;
}

export const setRoomsCount = (rooms: number): ISetRoomsCount => ({
  type: actionTypes.SET_ROOMS_COUNT,
  rooms,
});

export interface ISetPetsCount {
  type: actionTypes.SET_PETS_COUNT;
  pets: number;
}

export const setPetsCount = (pets: number): ISetPetsCount => ({
  type: actionTypes.SET_PETS_COUNT,
  pets,
});

export interface IFetchCalendarBuckets {
  type: actionTypes.FETCH_CALENDAR_BUCKETS;
}

export const fetchCalendarBuckets = (): IFetchCalendarBuckets => ({
  type: actionTypes.FETCH_CALENDAR_BUCKETS,
});

export interface ISetCalendarBuckets {
  type: actionTypes.SET_CALENDAR_BUCKETS;
  buckets: CalendarPriceBucket[];
  monthBuckets: IMonthBucket[];
}

export const setCalendarBuckets = (
  buckets: CalendarPriceBucket[],
  monthBuckets: IMonthBucket[]
): ISetCalendarBuckets => ({
  type: actionTypes.SET_CALENDAR_BUCKETS,
  buckets,
  monthBuckets,
});

export interface ISetCalendarBucketsError {
  type: actionTypes.SET_CALENDAR_BUCKETS_ERROR;
  error: boolean;
}

export const setCalendarBucketsError = (
  error: boolean
): ISetCalendarBucketsError => ({
  type: actionTypes.SET_CALENDAR_BUCKETS_ERROR,
  error,
});

export interface ISetApplyUserHotelPreferences {
  type: actionTypes.SET_APPLY_USER_HOTEL_PREFERENCES;
  applyPreferences: boolean;
}

export const setApplyUserHotelPreferences = (
  applyPreferences: boolean
): ISetApplyUserHotelPreferences => ({
  type: actionTypes.SET_APPLY_USER_HOTEL_PREFERENCES,
  applyPreferences,
});

export interface IFetchHasSeenVRAwareness {
  type: actionTypes.FETCH_HAS_SEEN_VR_AWARENESS;
}

export const fetchHasSeenVRAwareness = (): IFetchHasSeenVRAwareness => ({
  type: actionTypes.FETCH_HAS_SEEN_VR_AWARENESS,
});

export interface ISetHasSeenVRAwareness {
  type: actionTypes.SET_HAS_SEEN_VR_AWARENESS;
  hasSeenVRAwareness: boolean;
}

export const setHasSeenVRAwareness = (
  hasSeenVRAwareness: boolean
): ISetHasSeenVRAwareness => ({
  type: actionTypes.SET_HAS_SEEN_VR_AWARENESS,
  hasSeenVRAwareness,
});

export interface IUpdateHasSeenVRAwareness {
  type: actionTypes.UPDATE_HAS_SEEN_VR_AWARENESS;
  hasSeenVRAwareness: boolean;
}

export const updateHasSeenVRAwareness = (
  hasSeenVRAwareness: boolean
): IUpdateHasSeenVRAwareness => ({
  type: actionTypes.UPDATE_HAS_SEEN_VR_AWARENESS,
  hasSeenVRAwareness,
});

export interface ISetInitialStayType {
  type: actionTypes.SET_INITIAL_STAY_TYPE;
  stayType?: StayType;
}

export const setInitialStayType = (
  stayType?: StayType
): ISetInitialStayType => ({
  type: actionTypes.SET_INITIAL_STAY_TYPE,
  stayType,
});

export interface ISetSearch {
  type: actionTypes.SET_SEARCH;
  search: Partial<IHotelSearchState>;
}

export const setSearch = (search: Partial<IHotelSearchState>): ISetSearch => ({
  type: actionTypes.SET_SEARCH,
  search,
});

export type HotelSearchActions =
  | IFetchLocationCategories
  | ISetLocationCategories
  | ISetLocation
  | ISetFromDate
  | ISetUntilDate
  | ISetOccupancyCounts
  | ISetLocationAutocompleteError
  | ISetRoomsCount
  | ISetPetsCount
  | IFetchCalendarBuckets
  | ISetCalendarBuckets
  | ISetCalendarBucketsError
  | ISetApplyUserHotelPreferences
  | IFetchHasSeenVRAwareness
  | ISetHasSeenVRAwareness
  | IUpdateHasSeenVRAwareness
  | ISetInitialStayType
  | ISetSearch;
